/**
 * @file project/exporter.ts
 * @description Logic for exporting the active project into various image formats.
 */

import { g, layers } from '@hcie/core';
import { imageFormatRegistry } from '../format-registry';
import { DecodedImage } from '../format-interface';
import { ProjectSerializer } from './serializer';

export class ProjectExporter {
  static async exportImage(formatExt: string): Promise<Uint8Array | string> {
    const format = imageFormatRegistry.getByExtension(formatExt);
    
    if (formatExt === ".hcie" || !format || !format.canWrite) {
        return await ProjectSerializer.saveProject();
    }

    const canvas = document.createElement("canvas");
    canvas.width = g.image_width;
    canvas.height = g.image_height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not create flatten context");

    // Flatten all visible layers
    for (const layer of layers) {
        if (layer.visible) {
            ctx.globalAlpha = layer.opacity;
            ctx.globalCompositeOperation = (layer.blendMode as any) || "source-over";
            ctx.drawImage(layer.canvas as any, (layer as any).x || 0, (layer as any).y || 0);
        }
    }

    const flattenedData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    
    // Construct the full DecodedImage for formats that support layers
    const decoded: DecodedImage = {
        width: g.image_width,
        height: g.image_height,
        composite: flattenedData,
        layers: layers.map(l => ({
            name: l.name,
            canvas: l.canvas,
            visible: l.visible,
            opacity: l.opacity,
            blendMode: l.blendMode,
            x: (l as any).x || 0,
            y: (l as any).y || 0
        }))
    };

    const buffer = await format.write(decoded);
    return new Uint8Array(buffer);
  }
}
